"use client";

import { featureCollection, feature } from "@turf/turf";
import type { FeatureCollection, Feature, Geometry } from "geojson";
import { sampleSize } from "lodash";
import { useSite } from "src/hooks/sites";
import {
  type Site,
  type Observatory,
  SiteTypeEnum,
  ThemeTypeEnum,
} from "@/types";

function useSites(sites: Site[]) {
  const _sites = sites;
  const { getPoint } = useSite();

  const getSitesPointFeatureCollection = (sites: Site[]): FeatureCollection => {
    const geojsonFeatures: Feature[] = [];
    sites.forEach((site) => {
      const point = getPoint(site);
      if (point) geojsonFeatures.push(point);
    });
    return featureCollection(geojsonFeatures);
  };

  const getSitesGeometryFeatureCollection = (
    sites: Site[],
  ): FeatureCollection => {
    const features: Feature[] = [];
    sites.forEach((site) => {
      if (site.geometry) {
        const geometry = feature(site.geometry as Geometry);
        features.push(geometry);
      }
    });
    return featureCollection(features);
  };

  const filterSites = (
    observatories: Observatory[],
    observatory: Observatory | null,
  ): Site[] => {
    let filteredSites = sites;
    if (observatories.length) {
      filteredSites = filterSitesByObservatories(observatories, filteredSites);
    }

    if (observatory) {
      filteredSites = filterSitesByObservatory(observatory, filteredSites);
    }

    return filteredSites;
  };

  const filterSitesByThemes = (sites?: Site[]) => {
    const data = sites || _sites;
    return {
      earth: filterSitesByTheme(ThemeTypeEnum.EARTH, data),
      water: filterSitesByTheme(ThemeTypeEnum.WATER, data),
      universe: filterSitesByTheme(ThemeTypeEnum.UNIVERSE, data),
      biodiversity: filterSitesByTheme(ThemeTypeEnum.BIODIVERSITY, data),
    };
  };

  const filterSitesByTheme = (theme: ThemeTypeEnum, sites?: Site[]): Site[] => {
    const data = sites || _sites;
    return data.filter((site) => site.theme === theme);
  };

  const filterSitesByType = (type: SiteTypeEnum, sites?: Site[]): Site[] => {
    const data = sites || _sites;
    return data.filter((site) => site.type === type);
  };

  const filterSitesByObservatory = (
    observatory: Observatory,
    sites?: Site[],
  ): Site[] => {
    const data = sites || _sites;
    return data.filter((site) => observatory.id === site.observatoryId);
  };

  const filterSitesByObservatories = (
    observatories: Observatory[],
    sites?: Site[],
  ): Site[] => {
    const data = sites || _sites;
    return data.filter((site) =>
      observatories.some(
        (observatory) => observatory.id === site.observatoryId,
      ),
    );
  };

  const sampleSites = (n = 8): Site[] => {
    return sampleSize(sites, n);
  };

  return {
    getSitesPointFeatureCollection,
    getSitesGeometryFeatureCollection,
    filterSites,
    filterSitesByTheme,
    filterSitesByThemes,
    filterSitesByType,
    filterSitesByObservatory,
    filterSitesByObservatories,
    sampleSites,
  };
}

export { useSites };
